import { Injectable, EventEmitter, NgZone } from '@angular/core';
import { AppointmentsServiceV2 } from 'Public_old/src/app/Services/api/v2/appointments.service';
import { GeneralService } from 'Public_old/src/app/Services/general.service';
import { AppointmentApiModel } from '../../Services/api/v2/Models/AppointmentApiModel';
import { AppointmentAccessApiModel } from '../../Services/Models/General/AppointmentAccessApiModel';
import { ResourceApiModel } from '../../Models/Resources/ResourceApiModel';
import { OtherParticipantModel } from '../../Services/api/v2/Models/OtherParticipantModel';
import { TranslateService } from 'Public_old/src/app/Common/Services/translate.service';
import { DateService } from 'Public_old/src/app/Services/date.service';
import type { FileAttachment, Message } from '@routes/appointment/chat.controller';

interface AppointmentApiModelModified extends AppointmentApiModel {
	Resource: ResourceApiModel;
	Me: OtherParticipantModel | null;
}
export type Callback = (data: boolean) => void;
export type Disposer = () => void;
export interface FormData {
	id: number;
	respondantId: number;
	completed: boolean;
}

@Injectable()
export class AppointmentFlowService {
	public appointmentInfo: AppointmentApiModelModified = null;
	public accessInfo: AppointmentAccessApiModel;
	public subscriptions: Array<any> = [];
	public flow = 0;
	public readonly EXPECTED_FLOW = [
		'none',
		'beforeCallHasPayment',
		'beforeCallHasDynamicForms',
		'__INJECT__',
		'afterCallDynamicForms',
		'afterCallEvaluation',
		'couldNotReachCall',
	] as const;

	public beforeCallDynamicForms: FormData[] = [];
	public afterCallDynamicForms: FormData[] = [];

	// ---- need to figure out if below is needed ----

	// Is this needed?
	public onCallStateChange: EventEmitter<string> = new EventEmitter<string>();
	// Is this needed?
	public onDynamicFormCompleted = new EventEmitter<number>();
	// Is this needed?
	public onNoAccess: EventEmitter<void> = new EventEmitter<void>();
	// Is this needed?
	public onShowViewImages = new EventEmitter<{ images: Array<any>; startIndex: number }>();
	// Is this needed?
	public show = {
		sharedFiles: false,
		chat: false,
		chatLanguageSelectOverlayClose: true, // When triggered externally, force selection
		chatLanguageSelectOverlay: false,
		fullScreen: false,
		partnerOverlay: false,
		sipInviteBox: false,
		changeDevice: false,
		appointmentMenu: false,
	};
	// Is this needed?
	public myID: number | null = null;
	// Is this needed?
	public chatFiles: FileAttachment[] = [];
	// Is this needed?
	public chatMessages: Message[] = [];
	private currentTicket: string | null = null;

	constructor(
		private _generalService: GeneralService,
		public appointmentService: AppointmentsServiceV2,
		public ngZone: NgZone,
		public dateService: DateService,
		public translateService: TranslateService,
	) {
		// Empty
	}

	public initiateAppointment(ticket: string): void {
		this.currentTicket = ticket;

		this._generalService.getAppointmentAccessInfo(ticket).subscribe(
			(response) => {
				this.accessInfo = response.data;

				this.appointmentService.get(ticket).subscribe(
					(response) => {
						const { data } = response;
						const allParticipants = [data.MyParticipant, ...data.OtherParticipants];

						// The owner resource is the participant whose resource ID is the same as the booking
						// resource ID. In some cases (such as drop-in and Embassy v1 bookings), the booking
						// resource will be null, in which case we fall back to assuming the first participant
						// with a resource ID is the owner.
						const ownerResource: OtherParticipantModel =
							data.ResourceId !== null
								? allParticipants.find((p) => p.ResourceId === data.ResourceId)
								: allParticipants.find((p) => p.ResourceId !== null);

						const ownerResourceAsModel: ResourceApiModel = {
							Id: ownerResource.ResourceId,
							Title: ownerResource.Title,
							Name: ownerResource.Name,
							DescriptiveTitle: ownerResource.DescriptiveTitle,
							ProfileImageUrl: ownerResource.ProfileImageUrl,
							VideoProvider: data.VideoService,
							UnitId: data.UnitId,
						};

						this.appointmentInfo = {
							...data,
							Resource: ownerResourceAsModel,
							// 'Me' should only be set if the current user is a resource.
							Me: data.MyParticipant.ResourceId !== null ? data.MyParticipant : null,
						};

						this.beforeCallDynamicForms = this.appointmentInfo.Forms.filter((form) => form.Opportunity === 'BeforeBooking').map(
							(form) => ({
								completed: form.Completed,
								id: form.FormId,
								respondantId: form.RespondantId,
							}),
						);
						this.afterCallDynamicForms = this.appointmentInfo.Forms.filter((form) => form.Opportunity === 'AfterBooking').map(
							(form) => ({
								completed: form.Completed,
								id: form.FormId,
								respondantId: form.RespondantId,
							}),
						);

						this.checksBeforeCall();
					},
					(err) => {
						this.onNoAccess.emit();
					},
				);
			},
			(err) => {
				console.log(err);
			},
		);
	}

	public checksBeforeCall(): void {
		if (this.checkBeforeCallHasPayment()) {
			this.goToSpecificFlow('beforeCallHasPayment');

			return;
		}

		if (this.checkBeforeCallHasDynamicForms()) {
			this.goToSpecificFlow('beforeCallHasDynamicForms');

			return;
		}

		this.goToSpecificFlow('__INJECT__');
	}

	public checksAfterCall(): void {
		this.appointmentService.get(this.currentTicket).subscribe((res) => {
			this.afterCallDynamicForms = res.data.Forms.filter((form) => form.Opportunity === 'AfterBooking').map((form) => ({
				completed: form.Completed,
				id: form.FormId,
				respondantId: form.RespondantId,
			}));

			if (this.checkAfterCallDynamicForms()) this.goToSpecificFlow('afterCallDynamicForms');

			return;
		});

		this.goToSpecificFlow('afterCallEvaluation');
	}

	public goToSpecificFlow(state: AppointmentFlowService['EXPECTED_FLOW'][number]): void {
		this.ngZone.run(() => {
			const index = this.EXPECTED_FLOW.indexOf(state);
			this.flow = index;
		});
	}

	public goToNextFlow(): void {
		this.ngZone.run(() => {
			this.flow++;
		});
	}

	public setMessages(messages: Message[]): void {
		this.chatMessages = messages;
		let messagesFiles: FileAttachment[] = [];
		messages.map((message) => {
			if (message.files.length > 0) messagesFiles = [...messagesFiles, ...message.files];
		});
		this.chatFiles = messagesFiles;
	}

	public getDownloadUrl(fileId: string[]): string {
		// We used to get this link from backend but since we're not using WS in the evaluation page we do it like this.
		// Previous it looked something like this: this.webSocket.publish('getfilebatchurl', { fileId });

		//eg. https://fs1-dev1.vcare.pl/
		const fileServerUrl = this.chatFiles[0].downloadUrl.split('download')[0];

		let downloadParam = 'download/';

		if (fileId.length > 1) {
			downloadParam += 'batch?files=';
		}
		//eg. multiple files: https://fs1-dev1.vcare.pl/download/batch?files=c1b3fef5-5cac-473a-ade8-79bbb0fb1b8e,b8a52dc6-35ef-456d-8c61-2fa63ae316e1
		//single file: https://fs1-dev1.vcare.pl/download/c1b3fef5-5cac-473a-ade8-79bbb0fb1b8e
		const downloadUrl = fileServerUrl + downloadParam + fileId;

		return downloadUrl;
	}

	public getProfileImageFromId(id: number): string | null {
		if (this.appointmentInfo.Me && this.appointmentInfo.Me.ParticipantId === id) return this.appointmentInfo.Me.ProfileImageUrl;

		const participant = this.appointmentInfo.OtherParticipants.find((participant) => participant.ParticipantId === id);

		if (!participant) return null;

		return participant.ProfileImageUrl;
	}

	private checkBeforeCallHasPayment(): boolean {
		if (this.appointmentInfo.Role !== 'User') {
			return false;
		}

		if (this.appointmentInfo.Payment != null && this.appointmentInfo.Payment.State === 'Unpaid') {
			return true;
		}

		return false;
	}

	private checkBeforeCallHasDynamicForms(): boolean {
		if (this.appointmentInfo.Role !== 'User') return false;

		const hasUnfinishedForm = this.beforeCallDynamicForms.some((form) => !form.completed);

		return hasUnfinishedForm;
	}

	private checkAfterCallDynamicForms(): boolean {
		if (this.appointmentInfo.Role !== 'User') return false;

		const hasUnfinishedForm = this.afterCallDynamicForms.some((form) => !form.completed);

		return hasUnfinishedForm;
	}
	// ---- need to figure out if below is needed ----

	// Is this needed?
	public destroy(): void {
		this.appointmentInfo = null;
		this.accessInfo = null;

		// Is this needed?
		this.show = {
			sharedFiles: false,
			chat: false,
			chatLanguageSelectOverlayClose: true, // When triggered externally, force selection
			chatLanguageSelectOverlay: false,
			fullScreen: false,
			partnerOverlay: false,
			sipInviteBox: false,
			changeDevice: false,
			appointmentMenu: false,
		};
	}
}
